import { $fetchAlloRemote } from '@/services/api-allo/client-remote';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import { serializeError } from '@packages/client-sdk-react/src/voice/error-utils';
import type { HangupVerificationSource } from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useVoiceCallState } from '@packages/client-sdk-react/src/voice/voice-call-container';
import {
  getVonageSessionDiagnostics,
  useVonageClient,
} from '@packages/client-sdk-react/src/vonage-client-provider';
import { HangupReason } from '@vonage/client-sdk';
import { useCallback, useEffect, useRef } from 'react';

type ForceServerHangupContext = 'initial' | 'stuck_retry' | 'privacy_fallback';

type PhantomRecoveryPath = 'server-fallback' | 'privacy-fallback';

const log = createLogger('VerifiedHangup', ['calls']);

/**
 * Composite phantom-call telemetry.
 *
 * A phantom is "detected" whenever the verified-hangup flow needed to
 * escalate beyond the SDK's own hangup path (i.e., the backend force-hangup
 * or the 30s privacy fallback had to resolve the leg). This event joins the
 * symptom (which recovery path worked) to the cause (vonage client instance
 * count + sessions rotated during the call's lifetime). Under healthy
 * conditions `instanceCount` should be 1 and `sessionsSinceCallStart` should
 * be 0 — any phantom with different values points at a regression.
 */
const emitPhantomDetected = (
  callId: string,
  recoveryPath: PhantomRecoveryPath,
  extra?: Record<string, unknown>,
): void => {
  const diagnostics = getVonageSessionDiagnostics();
  log.error('Phantom call detected', {
    callId,
    recoveryPath,
    vonageInstanceCount: diagnostics.instanceCount,
    totalSessionCreateCount: diagnostics.totalSessionCreateCount,
    sessionsSinceCallStart: diagnostics.sessionsSinceCallStart,
    timeSinceLastSessionCreateMs: diagnostics.timeSinceLastSessionCreateMs,
    timestamp: Date.now(),
    ...extra,
  });
  safeTrack(CallEventName.CallPhantomDetected, CallEventLevel.Error, {
    deviceLegId: callId,
    recoveryPath,
    vonageInstanceCount: diagnostics.instanceCount,
    totalSessionCreateCount: diagnostics.totalSessionCreateCount,
    sessionsSinceCallStart: diagnostics.sessionsSinceCallStart,
    timeSinceLastSessionCreateMs: diagnostics.timeSinceLastSessionCreateMs,
    ...extra,
  });
};

const SERVER_HANGUP_TIMEOUT_MS = 5000;

// Module-level single-flight guard: one in-flight server call per callId
const inflightServerHangups = new Map<string, Promise<boolean>>();

const safeLogServerFallback = (
  context: Record<string, unknown>,
  level: 'info' | 'warn' | 'error' = 'info',
) => {
  try {
    log[level]('Server force-hangup fallback', context);
  } catch {
    // Never let telemetry errors escape
  }
};

const safeTrack = (
  name: CallEventName,
  level: CallEventLevel,
  payload: Record<string, unknown>,
) => {
  try {
    tracingService.trackEvent(name, level, payload);
  } catch {
    // Never let telemetry errors escape
  }
};

/**
 * Last-resort server-side hangup via backend API.
 *
 * Called when client-side SDK hangup fails and the call is still alive
 * server-side (e.g., SDK lost the call from its internal registry).
 * The backend uses Vonage Server SDK credentials to force-terminate the leg.
 *
 * - Times out after 5s (AbortController)
 * - Single-flight per callId: concurrent calls for the same callId share the same promise
 * - All errors and rejections are swallowed — this is a last-resort call
 * - 404 is treated as success (leg already terminated — idempotent semantics)
 */
const forceServerHangup = (callId: string, context: ForceServerHangupContext): Promise<boolean> => {
  const existing = inflightServerHangups.get(callId);
  if (existing) {
    safeLogServerFallback({
      callId,
      context,
      result: 'deduped',
      timestamp: Date.now(),
    });
    return existing;
  }

  const startedAt = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SERVER_HANGUP_TIMEOUT_MS);

  const promise = (async (): Promise<boolean> => {
    try {
      const { response } = await $fetchAlloRemote.POST('/v1/call/{legId}/hangup', {
        params: { path: { legId: callId } },
        signal: controller.signal,
      });

      // 2xx or 404 (already terminated) both mean the leg is dead server-side.
      const ok = response.ok || response.status === 404;
      const elapsedMs = Date.now() - startedAt;

      safeLogServerFallback({
        callId,
        context,
        status: response.status,
        result: ok ? 'success' : 'failed',
        elapsedMs,
        timestamp: Date.now(),
      });

      safeTrack(
        CallEventName.ServerForceHangup,
        ok ? CallEventLevel.Info : CallEventLevel.Warning,
        {
          deviceLegId: callId,
          context,
          status: response.status,
          result: ok ? 'success' : 'failed',
          elapsedMs,
        },
      );

      return ok;
    } catch (error) {
      const isAbort = error instanceof Error && error.name === 'AbortError';
      safeLogServerFallback(
        {
          callId,
          context,
          result: isAbort ? 'timeout' : 'error',
          elapsed_ms: Date.now() - startedAt,
          ...serializeError(error),
          timestamp: Date.now(),
        },
        'warn',
      );

      safeTrack(CallEventName.ServerForceHangup, CallEventLevel.Warning, {
        deviceLegId: callId,
        context,
        result: isAbort ? 'timeout' : 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      return false;
    } finally {
      clearTimeout(timeoutId);
      inflightServerHangups.delete(callId);
    }
  })();

  inflightServerHangups.set(callId, promise);
  return promise;
};

const HANGUP_TIMEOUT_MS = 8000;
// After failed hangup: poll immediately since we need to check if call is dead.
// 3 attempts over ~2s — then escalate to backend force-hangup.
const POLL_DELAYS_IMMEDIATE = [0, 500, 1500];
// After successful hangup: first poll at 500ms to give server time to process the kick.
// 3 attempts over ~2s — then escalate to backend force-hangup.
const POLL_DELAYS_AFTER_SUCCESS = [500, 500, 1000];
const STUCK_RETRY_INTERVAL_MS = 10_000;
const PRIVACY_FALLBACK_MS = 30_000;

const isLegCompleted = (status: string) => status.toLowerCase() === 'completed';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type EndCallOptions = {
  source: HangupVerificationSource;
};

export const useVerifiedHangup = () => {
  const [call, callDispatch] = useVoiceCallState();
  const vonageClient = useVonageClient();
  const hangupInProgressRef = useRef(false);
  const pendingHangupCallIdRef = useRef<string | undefined>(undefined);
  const verifiedRef = useRef(false);
  const cancelledRef = useRef(false);
  const stuckRetryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const privacyFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs for latest callback versions — used by online/visibility event listeners
  const verifyTerminationRef = useRef<
    ((callId: string, delays?: number[]) => Promise<boolean>) | null
  >(null);
  const handleVerifiedRef = useRef<((callId: string) => void) | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      if (stuckRetryTimerRef.current) {
        clearInterval(stuckRetryTimerRef.current);
      }
      if (privacyFallbackTimerRef.current) {
        clearTimeout(privacyFallbackTimerRef.current);
      }
    };
  }, []);

  // Listen for callHangup event as instant verification
  useEffect(() => {
    const handleCallHangup = vonageClient.on('callHangup', (callId, _quality, _reason) => {
      if (callId !== pendingHangupCallIdRef.current) {
        return;
      }

      const wasStuck = stuckRetryTimerRef.current !== null;

      if (wasStuck) {
        log.info('Hangup verification unstuck', {
          callId,
          resolution_method: 'callHangup_event',
          timestamp: Date.now(),
        });
      }

      log.info('Hangup verification confirmed', {
        callId,
        method: 'event',
        was_stuck: wasStuck,
        timestamp: Date.now(),
      });

      // Delegate to handleVerified so stuck timers are properly cleaned up
      handleVerifiedRef.current?.(callId);
    });

    return () => {
      vonageClient.off('callHangup', handleCallHangup);
    };
  }, [vonageClient]);

  // Re-verify on online/visibility events when stuck
  useEffect(() => {
    const retryIfStuck = () => {
      const callId = pendingHangupCallIdRef.current;
      if (!callId || verifiedRef.current) {
        return;
      }
      // Use refs to always call latest callback versions
      verifyTerminationRef.current?.(callId).then((confirmed) => {
        if (confirmed) {
          handleVerifiedRef.current?.(callId);
        }
      });
    };

    const onOnline = () => retryIfStuck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        retryIfStuck();
      }
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleVerified = useCallback(
    (verifiedCallId: string) => {
      verifiedRef.current = true;
      pendingHangupCallIdRef.current = undefined;

      if (stuckRetryTimerRef.current) {
        clearInterval(stuckRetryTimerRef.current);
        stuckRetryTimerRef.current = null;
      }
      if (privacyFallbackTimerRef.current) {
        clearTimeout(privacyFallbackTimerRef.current);
        privacyFallbackTimerRef.current = null;
      }

      // Eagerly stop local media tracks to release the microphone (clears macOS mic indicator).
      // replaceTrack() does not transfer ownership, so the peer connection's sender tracks
      // must be explicitly stopped even after Vonage SDK processes the hangup.
      try {
        const pc = vonageClient.getPeerConnection(verifiedCallId);
        if (pc) {
          for (const sender of pc.getSenders()) {
            sender.track?.stop();
          }
        }
      } catch {
        // Best effort — peer connection may already be gone
      }

      callDispatch({
        type: 'call:hangup:verified',
        reason: HangupReason.LocalHangup,
        callId: verifiedCallId,
      });
    },
    [callDispatch, vonageClient],
  );

  const verifyTermination = useCallback(
    async (callId: string, delays: number[] = POLL_DELAYS_IMMEDIATE): Promise<boolean> => {
      for (let i = 0; i < delays.length; i++) {
        if (verifiedRef.current || cancelledRef.current) {
          return verifiedRef.current;
        }

        const delay = delays[i] ?? 0;
        if (delay > 0) {
          await sleep(delay);
        }

        if (verifiedRef.current || cancelledRef.current) {
          return verifiedRef.current;
        }

        try {
          const leg = await vonageClient.getLeg(callId);
          const legStatus = typeof leg.status === 'string' ? leg.status : String(leg.status);

          callDispatch({
            type: 'call:hangup:verification:progress',
            attempts: i + 1,
            lastLegStatus: legStatus,
          });

          log.info('Hangup verification poll', {
            callId,
            attempt: i + 1,
            leg_status: legStatus,
            timestamp: Date.now(),
          });

          if (isLegCompleted(legStatus)) {
            log.info('Hangup verification confirmed', {
              callId,
              method: 'poll',
              attempts: i + 1,
              timestamp: Date.now(),
            });
            return true;
          }
        } catch (error) {
          callDispatch({
            type: 'call:hangup:verification:progress',
            attempts: i + 1,
            lastError: error instanceof Error ? error.message : String(error),
          });

          log.info('Hangup verification poll', {
            callId,
            attempt: i + 1,
            ...serializeError(error),
            timestamp: Date.now(),
          });
        }
      }
      return false;
    },
    [vonageClient, callDispatch],
  );

  // Keep refs in sync so online/visibility listeners always use latest versions
  verifyTerminationRef.current = verifyTermination;
  handleVerifiedRef.current = handleVerified;

  const triggerPrivacyFallback = useCallback(
    (callId: string) => {
      // Local teardown FIRST to protect user's privacy (stop mic audio immediately).
      // Server-side hangup fires in the background and must not block UI release.
      try {
        const pc = vonageClient.getPeerConnection(callId);
        if (pc) {
          for (const sender of pc.getSenders()) {
            sender.track?.stop();
          }
          pc.close();
        }
      } catch {
        // Best effort — peer connection may already be gone
      }

      // Last-resort server-side hangup, fire-and-forget (single-flight deduped)
      forceServerHangup(callId, 'privacy_fallback').catch(() => {
        // forceServerHangup swallows errors internally, but guard against rejection
      });

      log.info('Hangup verification privacy fallback', {
        callId,
        timestamp: Date.now(),
      });

      // Stop background retry — we've given up
      if (stuckRetryTimerRef.current) {
        clearInterval(stuckRetryTimerRef.current);
        stuckRetryTimerRef.current = null;
      }

      // Force-resolve UI state so user can make new calls
      verifiedRef.current = true;
      pendingHangupCallIdRef.current = undefined;
      hangupInProgressRef.current = false;
      callDispatch({
        type: 'call:hangup:verified',
        reason: 'LOCAL_ERROR',
        callId,
      });
      // Nothing worked for 30s; we gave up and forced the UI to idle.
      // This is the strongest phantom signal.
      emitPhantomDetected(callId, 'privacy-fallback');
    },
    [vonageClient, callDispatch],
  );

  const startStuckRetry = useCallback(
    (callId: string) => {
      // Clear any existing timers before starting new ones
      if (stuckRetryTimerRef.current) {
        clearInterval(stuckRetryTimerRef.current);
        stuckRetryTimerRef.current = null;
      }
      if (privacyFallbackTimerRef.current) {
        clearTimeout(privacyFallbackTimerRef.current);
        privacyFallbackTimerRef.current = null;
      }

      callDispatch({ type: 'call:hangup:verification:stuck' });

      log.info('Hangup verification stuck', {
        callId,
        timestamp: Date.now(),
      });

      // Background retry every 10s (tick-level single-flight to avoid overlap)
      let tickInFlight = false;
      stuckRetryTimerRef.current = setInterval(async () => {
        if (verifiedRef.current) {
          if (stuckRetryTimerRef.current) {
            clearInterval(stuckRetryTimerRef.current);
            stuckRetryTimerRef.current = null;
          }
          return;
        }

        if (tickInFlight) {
          return;
        }
        tickInFlight = true;

        try {
          // Try hangup again + backend force-hangup + single getLeg check
          try {
            await vonageClient.hangup(callId);
          } catch {
            // Expected — call may not exist in SDK registry
          }

          if (verifiedRef.current || cancelledRef.current) {
            return;
          }

          const serverHangupOk = await forceServerHangup(callId, 'stuck_retry');

          if (verifiedRef.current || cancelledRef.current) {
            return;
          }

          // Same short-circuit as Step 2.5: trust the server's "leg is dead"
          // response instead of re-polling via getLeg.
          if (serverHangupOk) {
            log.info('Hangup verification unstuck', {
              callId,
              resolution_method: 'background_retry',
              timestamp: Date.now(),
            });
            handleVerified(callId);
            emitPhantomDetected(callId, 'server-fallback', {
              phase: 'stuck_retry',
            });
            return;
          }

          try {
            const leg = await vonageClient.getLeg(callId);
            if (isLegCompleted(typeof leg.status === 'string' ? leg.status : String(leg.status))) {
              log.info('Hangup verification unstuck', {
                callId,
                resolution_method: 'background_retry',
                timestamp: Date.now(),
              });
              handleVerified(callId);
              emitPhantomDetected(callId, 'server-fallback', {
                phase: 'stuck_retry',
              });
            }
          } catch {
            // Continue retrying
          }
        } finally {
          tickInFlight = false;
        }
      }, STUCK_RETRY_INTERVAL_MS);

      // Privacy fallback after 30s
      privacyFallbackTimerRef.current = setTimeout(() => {
        if (!verifiedRef.current) {
          triggerPrivacyFallback(callId);
        }
      }, PRIVACY_FALLBACK_MS);
    },
    [vonageClient, callDispatch, handleVerified, triggerPrivacyFallback],
  );

  const endCall = useCallback(
    async (opts: EndCallOptions): Promise<boolean> => {
      const callId = call.callId;

      if (hangupInProgressRef.current) {
        log.info('Duplicate hangup attempt blocked', {
          callId,
          timestamp: Date.now(),
        });
        return false;
      }

      if (!callId) {
        // No call to hang up — reset to idle if stuck in non-idle state
        if (call.callState !== 'idle') {
          callDispatch({ type: 'call:hangup:verified', reason: HangupReason.LocalHangup });
        }
        return true;
      }

      hangupInProgressRef.current = true;
      verifiedRef.current = false;
      pendingHangupCallIdRef.current = callId;

      try {
        callDispatch({ type: 'call:hangup:verification:start', source: opts.source });

        log.info('Hangup verification started', {
          callId,
          source: opts.source,
          callState: call.callState,
          timestamp: Date.now(),
        });

        tracingService.trackEvent(CallEventName.AttemptHangup, CallEventLevel.Info, {
          deviceLegId: callId,
        });

        // Step 1: Attempt SDK hangup
        let hangupSucceeded = false;
        try {
          await Promise.race([
            vonageClient.hangup(callId),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('hangup_timeout')), HANGUP_TIMEOUT_MS),
            ),
          ]);

          hangupSucceeded = true;
          log.info('SDK hangup request succeeded', {
            callId,
            timestamp: Date.now(),
          });
        } catch (error) {
          log.info('SDK hangup request failed', {
            callId,
            ...serializeError(error),
            timestamp: Date.now(),
          });
          // Don't return — still need to verify via getLeg even if hangup() failed
        }

        // Step 2: Verify termination (if event hasn't already confirmed)
        // After successful hangup, use delayed first poll (500ms) to give server
        // time to process the kick. After failed hangup, poll immediately.
        if (!verifiedRef.current) {
          const delays = hangupSucceeded ? POLL_DELAYS_AFTER_SUCCESS : POLL_DELAYS_IMMEDIATE;
          const confirmed = await verifyTermination(callId, delays);

          if (confirmed && !verifiedRef.current) {
            handleVerified(callId);
          }
        }

        // Step 2.5: backend force-hangup. A 2xx/404 means the leg is dead
        // server-side — trust it and short-circuit. Re-polling via getLeg is
        // doomed when the SDK's call registry is the broken side (network
        // drop, mid-call session rotation). On HTTP failure (5xx, network)
        // fall through to re-poll because partial side effects may still
        // have ended the leg.
        if (!verifiedRef.current) {
          const serverHangupOk = await forceServerHangup(callId, 'initial');
          if (serverHangupOk && !verifiedRef.current) {
            handleVerified(callId);
            emitPhantomDetected(callId, 'server-fallback', {
              phase: 'initial',
              source: opts.source,
            });
          } else if (!verifiedRef.current && !cancelledRef.current) {
            const confirmed = await verifyTermination(callId, [500, 1000, 2000]);
            if (confirmed && !verifiedRef.current) {
              handleVerified(callId);
              emitPhantomDetected(callId, 'server-fallback', {
                phase: 'initial',
                source: opts.source,
              });
            }
          }
        }

        // Step 3: If still not verified, enter stuck state
        if (!verifiedRef.current) {
          startStuckRetry(callId);
          return false;
        }

        return true;
      } finally {
        hangupInProgressRef.current = false;
      }
    },
    [
      call.callId,
      call.callState,
      callDispatch,
      vonageClient,
      verifyTermination,
      handleVerified,
      startStuckRetry,
    ],
  );

  return { endCall };
};
